import type { CustomCSSProperties } from "@iyk/ui/types"
import type { Route } from "./+types/collection.ts"

import * as UI from "@iyk/ui"
import * as React from "react"

import { Id } from "@iyk/prelude"
import type { User } from "@iyk/vf-core"
import { Res } from "@iyk/vf-server"
import { Effect } from "effect"
import { appConfig } from "../../app-config.ts"
import { Handler } from "../../server/handler.ts"
import { Profile } from "../profile.ts"
import { BackToProfileButton } from "../ui/back-to-profile-button.tsx"
import { CollectionCarousel } from "../ui/collection-carousel.tsx"
import { CollectionShare } from "../ui/collection-share.tsx"
import { CollectionSheet } from "../ui/collection-sheet.tsx"
import { EmptyCollection } from "../ui/empty-collection.tsx"
import { Link, Routes } from "../ui/link.tsx"
import stylesheet from "../ui/profile.css?url"
import { UserProfile } from "../ui/user-profile.tsx"

export const links: Route.LinksFunction = () => [{ rel: "stylesheet", href: stylesheet }]

export const meta: Route.MetaFunction = ({ params, data }) => {
  const { username } = data.collection
  const { profileId } = params

  const shareUrl = `${data.appUrl}/profile/${profileId}`
  const shareTitle = `${username} - ${appConfig.artist.name} Collection`
  const shareDescription = `${username} - ${appConfig.artist.name} Collection`
  const shareImage = appConfig.assets.images.og

  return [
    // Open Graph
    {
      property: "og:title",
      content: shareTitle
    },
    {
      property: "og:description",
      content: shareDescription
    },
    {
      property: "og:type",
      content: "website"
    },
    {
      property: "og:url",
      content: shareUrl
    },
    {
      property: "og:image",
      content: shareImage
    },
    {
      property: "og:width",
      content: "1200"
    },
    {
      property: "og:height",
      content: "630"
    },
    // Twitter
    {
      property: "twitter:card",
      content: "summary_large_image"
    },
    {
      property: "twitter:title",
      content: shareTitle
    },
    {
      property: "twitter:description",
      content: shareDescription
    },
    {
      property: "twitter:image",
      content: shareImage
    },
    {
      property: "twitter:width",
      content: "1200"
    },
    {
      property: "twitter:height",
      content: "630"
    }
  ]
}

type LoaderData = {
  collection: Profile.Collection
  appUrl: string
}

export const loader = Handler.make<LoaderData>("profile-collection", ({ params, api, config }) => {
  return Effect.gen(function* () {
    if (!params.profileId) {
      return yield* Effect.dieMessage("No profile ID in params")
    }

    const userId = Id.make<User>(params.profileId)

    return yield* api
      .findCollection(userId)
      .pipe(Effect.map((collection) => Res.json({ collection, appUrl: config.app.url.href })))
  }).pipe(Effect.catchTags({ "User/NotFound": () => Effect.succeed(Res.notFound) }))
})

export default function PublicLayout(props: Route.ComponentProps) {
  const { username, collectables, totalCheckIns, totalMerchOwned } = props.loaderData.collection

  const [currentCollectionIndex, setCurrentCollectionIndex] = React.useState(0)
  const [openCollectionSheet, setOpenCollectionSheet] = React.useState(false)

  if (collectables.length === 0) {
    return <EmptyCollection name={username} />
  }

  const [backgroundColor, setBackgroundColor] = React.useState<string>(
    collectables[0]?.color || "transparent"
  )

  const rootStyles: CustomCSSProperties = {
    "--background-color": backgroundColor
  }

  return (
    <div
      className={UI.cx([
        "max-w-[var(--app-max-width)] bg-[var(--background-color)]",
        "relative pb-[46px] h-dvh w-full mx-auto group",
        "grid grid-rows-[auto_auto_1fr_auto] gap-2",
        "data-[show=true]:bg-gray-12",
        "transition-[background-color] duration-500"
      ])}
      style={rootStyles}
      data-one-collectable={collectables.length === 1}
    >
      <Link className="text-gray-12 w-full h-[var(--header-height)] px-4 flex items-center" to={Routes.home}>
        <img
          src={appConfig.assets.images.logo.url}
          style={{ width: `${appConfig.assets.images.logo.width}px` }}
          alt={`${appConfig.artist.name} logo`}
        />
      </Link>
      <span className="px-4">
        <BackToProfileButton />
      </span>

      <CollectionCarousel.Root
        onSlideChange={(index) => {
          setCurrentCollectionIndex(index)
          setBackgroundColor(collectables[index].color)
        }}
        onSlideClick={() => setOpenCollectionSheet(true)}
      >
        <CollectionCarousel.Container>
          <CollectionCarousel.Slides data={collectables}>
            {(collectable) => (
              <img
                src={collectable.imageSrc}
                alt={collectable.title}
                className="object-contain mx-auto aspect-square"
              />
            )}
          </CollectionCarousel.Slides>
        </CollectionCarousel.Container>
        <span className="flex flex-col gap-10">
          <UserProfile name={username} />
          {collectables.length > 1 && <CollectionCarousel.Lines />}
        </span>
      </CollectionCarousel.Root>
      <CollectionShare username={username} totalCheckIns={totalCheckIns} totalMerchOwned={totalMerchOwned} />
      <CollectionSheet
        collectable={collectables[currentCollectionIndex]}
        open={openCollectionSheet}
        onOpenChange={setOpenCollectionSheet}
      />
    </div>
  )
}
